import type { AppStore } from '@/store';
import { isSupportPreviewBrowserRoute } from '@/http/supportPreviewRequestGuard';
import { AuthActionCreators } from '@/store/actionCreators/authCreators';
import { AuthSlice } from '@/store/reducers/AuthReducer';
import { UserAvatarSlice } from '@/store/reducers/UserAvatarReducer';
import { reloadAfterForeignAccountChange } from '@srm/ui/shell/AccountSwitch';
import { auth } from './index';
import { isMultiAccountEnabled } from './multiAccount';

/**
 * Сессия входа ↔ стор. Вызвать один раз после создания стора и до монтирования: в cookie-режиме промис ждёт тихую
 * ротацию после перезагрузки, чтобы ProtectedRoute сразу получил корректный `isAuth` и не увёл на /login раньше
 * времени. Отдельным модулем от `@/auth`: стор → authCreators → http → `@/auth` иначе замкнулся бы в цикл.
 */
export function startAuth(store: AppStore): Promise<void> {
    const expire = (cause?: 'user-switch') => {
        store.dispatch(AuthSlice.actions.sessionExpired(cause));
        store.dispatch(UserAvatarSlice.actions.clearAvatar());
    };

    // Вход в соседней вкладке, пока здесь экран входа (EUPDEV-12114): профиль загружен — ProtectedRoute пропустит,
    // LoginForm уйдёт на свой returnUrl. Профиль не загрузился (токен к запросу уже протух) — полусессию не оставляем:
    // токен остаётся у соседней вкладки (body — общий localStorage), но сессией этой вкладки не считается, и маршрут
    // «только по токену» (предпросмотр поддержки) без профиля не пропустит.
    const loadForeignUser = async (accessToken: string) => {
        await store.dispatch(AuthActionCreators.getCurrentUser(accessToken));
        if (store.getState().auth.isAuth) return;
        expire();
        store.dispatch(AuthSlice.actions.accessTokenChanged(false));
    };

    const started = auth.start({
        isSignedIn: () => store.getState().auth.isAuth,
        onAccessTokenChange: (accessToken) => {
            store.dispatch(AuthSlice.actions.accessTokenChanged(!!accessToken));
        },
        onSessionExpired: () => expire(),
        // Предпросмотр поддержки во фрейме профиль не грузит (обычные запросы ему закрыты): иначе каждая ротация
        // родительской вкладки включала бы загрузку и перемонтировала предпросмотр.
        onForeignLogin: (accessToken) =>
            isSupportPreviewBrowserRoute(window.location.pathname) ? undefined : loadForeignUser(accessToken),
        // В соседней вкладке вошёл другой пользователь — здесь сессия прежнего кончилась, грузим профиль нового.
        // Мультиаккаунт: переключение учётной записи — перезагрузка на том же адресе (гаснут хабы, кэши и стор
        // прежнего), при несохранённой правке — экран «Учётная запись сменилась».
        onUserSwitch: async (accessToken) => {
            if (isMultiAccountEnabled()) {
                reloadAfterForeignAccountChange();
                return;
            }
            expire('user-switch');
            await loadForeignUser(accessToken);
        },
        onApiUnavailable: () => {
            store.dispatch(AuthSlice.actions.setApiUnavailable(true));
        },
    });
    store.dispatch(AuthSlice.actions.accessTokenChanged(!!auth.getAccessToken()));
    return started;
}
